import {
  Box,
  Button,
  Dialog,
  Flex,
  Heading,
  Input,
  NativeSelect,
  Portal,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { StateBadge } from '../components/Badges';
import { HostInput, HostState, PublicHostEntry } from '../types';

const EMPTY_FORM: HostInput = { name: '', baseUrl: '', serviceApiSecret: '', state: 'testing', sites: [] };

interface DialogState {
  mode: 'create' | 'edit';
  originalName?: string;
  form: HostInput;
  /** Comma-separated sites field kept as a string while editing for simpler typing. */
  sitesText: string;
}

function parseSitesText(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(',')) {
    const name = part.trim();
    if (!name || seen.has(name)) {
      continue;
    }
    seen.add(name);
    out.push(name);
  }
  return out;
}

function HostDialog({
  state,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  state: DialogState;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (form: HostInput) => void;
}) {
  const [form, setForm] = useState<HostInput>(state.form);
  const [sitesText, setSitesText] = useState(state.sitesText);
  const isEdit = state.mode === 'edit';

  return (
    <Dialog.Root open onOpenChange={event => (!event.open ? onClose() : undefined)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW='md'>
            <Dialog.Header>
              <Dialog.Title>{isEdit ? `Edit host "${state.originalName}"` : 'Add host'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Flex direction='column' gap={3}>
                <Box>
                  <Text fontSize='sm' mb={1}>
                    Name
                  </Text>
                  <Input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder='woburn'
                  />
                </Box>
                <Box>
                  <Text fontSize='sm' mb={1}>
                    Base URL
                  </Text>
                  <Input
                    value={form.baseUrl}
                    onChange={e => setForm({ ...form, baseUrl: e.target.value })}
                    placeholder='https://woburn.agencyrobotics.com'
                  />
                </Box>
                <Box>
                  <Text fontSize='sm' mb={1}>
                    Service API secret {isEdit ? '(leave blank to keep the current secret)' : ''}
                  </Text>
                  <Input
                    type='password'
                    value={form.serviceApiSecret ?? ''}
                    onChange={e => setForm({ ...form, serviceApiSecret: e.target.value })}
                    placeholder={isEdit ? '(unchanged)' : "host's SERVICE_API_SECRET"}
                  />
                </Box>
                <Box>
                  <Text fontSize='sm' mb={1}>
                    Sites (comma-separated)
                  </Text>
                  <Input value={sitesText} onChange={e => setSitesText(e.target.value)} placeholder='alpha, beta' />
                  <Text fontSize='xs' color='gray.500' mt={1}>
                    Sites this host actually runs. Required when several hosts share one database so other hosts&apos;
                    sites are not listed here.
                  </Text>
                </Box>
                <Box>
                  <Text fontSize='sm' mb={1}>
                    State (visual only)
                  </Text>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={form.state}
                      onChange={e => setForm({ ...form, state: e.target.value as HostState })}
                    >
                      <option value='testing'>testing</option>
                      <option value='deployed'>deployed</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Box>
                {error ? <Text color='red.500'>{error}</Text> : null}
              </Flex>
            </Dialog.Body>
            <Dialog.Footer gap={2}>
              <Button variant='outline' onClick={onClose} disabled={pending}>
                Cancel
              </Button>
              <Button
                colorPalette='primary'
                onClick={() => onSubmit({ ...form, sites: parseSitesText(sitesText) })}
                loading={pending}
              >
                {isEdit ? 'Save' : 'Add host'}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default function ManageHosts() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['hosts'], queryFn: api.hosts });
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['hosts'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
    void queryClient.invalidateQueries({ queryKey: ['metrics'] });
  };

  const saveMutation = useMutation({
    mutationFn: (input: { state: DialogState; form: HostInput }) => {
      const payload = { ...input.form };
      if (input.state.mode === 'edit' && !payload.serviceApiSecret) {
        delete payload.serviceApiSecret;
      }
      return input.state.mode === 'create'
        ? api.addHost(payload)
        : api.updateHost(input.state.originalName as string, payload);
    },
    onSuccess: () => {
      invalidate();
      setDialog(null);
      setFormError(null);
    },
    onError: (error: Error) => setFormError(error.message),
  });

  const removeMutation = useMutation({
    mutationFn: (name: string) => api.removeHost(name),
    onSuccess: invalidate,
  });

  if (isLoading) {
    return <Spinner />;
  }

  const hosts = data?.hosts ?? [];

  return (
    <Flex direction='column' gap={4} maxW='1000px'>
      <Flex align='center'>
        <Heading size='xl' flex={1}>
          Manage hosts
        </Heading>
        <Button
          colorPalette='primary'
          onClick={() => {
            setFormError(null);
            setDialog({ mode: 'create', form: EMPTY_FORM, sitesText: '' });
          }}
        >
          Add host
        </Button>
      </Flex>
      <Text fontSize='sm' color='gray.500'>
        Changes are written to the hosts file and take effect immediately — no restart required.
      </Text>

      <Table.Root size='sm' variant='line'>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Name</Table.ColumnHeader>
            <Table.ColumnHeader>Base URL</Table.ColumnHeader>
            <Table.ColumnHeader>Sites</Table.ColumnHeader>
            <Table.ColumnHeader>State</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='right'>Actions</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {hosts.map((host: PublicHostEntry) => (
            <Table.Row key={host.name}>
              <Table.Cell fontWeight='medium'>{host.name}</Table.Cell>
              <Table.Cell>{host.baseUrl}</Table.Cell>
              <Table.Cell>{host.sites.length > 0 ? host.sites.join(', ') : '—'}</Table.Cell>
              <Table.Cell>
                <StateBadge state={host.state} />
              </Table.Cell>
              <Table.Cell textAlign='right'>
                <Flex gap={2} justify='flex-end'>
                  <Button
                    size='xs'
                    variant='outline'
                    onClick={() => {
                      setFormError(null);
                      setDialog({
                        mode: 'edit',
                        originalName: host.name,
                        form: {
                          name: host.name,
                          baseUrl: host.baseUrl,
                          serviceApiSecret: '',
                          state: host.state,
                          sites: host.sites,
                        },
                        sitesText: host.sites.join(', '),
                      });
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size='xs'
                    colorPalette='red'
                    variant='outline'
                    loading={removeMutation.isPending && removeMutation.variables === host.name}
                    onClick={() => {
                      if (window.confirm(`Remove host "${host.name}" from the dashboard?`)) {
                        removeMutation.mutate(host.name);
                      }
                    }}
                  >
                    Remove
                  </Button>
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
          {hosts.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={5}>
                <Text color='gray.500'>No hosts yet. Add your first deployment.</Text>
              </Table.Cell>
            </Table.Row>
          ) : null}
        </Table.Body>
      </Table.Root>

      {dialog ? (
        <HostDialog
          key={`${dialog.mode}:${dialog.originalName ?? 'new'}`}
          state={dialog}
          pending={saveMutation.isPending}
          error={formError}
          onClose={() => {
            setDialog(null);
            setFormError(null);
          }}
          onSubmit={form => saveMutation.mutate({ state: dialog, form })}
        />
      ) : null}
    </Flex>
  );
}
